"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Notification as NotificationIcon } from "iconsax-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import Link from "next/link";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";
import { SupportedLanguage } from "@/lib/shared";
import lang from "../lang.json";
// import {
//   getNotifications,
//   getUnreadCounter,
//   readNotificationById,
// } from "@/actions/notifications";
import { TNotification } from "@/actions/types";
import { cn } from "@/lib/utils";
import { getNotifications } from "@/actions/notifications";
import Image from "next/image";
import { ScrollArea } from "@/components/ui/scroll-area";

// Helper function to check if string is a valid date
const isValidDate = (dateString: string): boolean => {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

// Helper function to format date to relative time (1y, 1d, 1w)
const formatRelativeTime = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const distance = formatDistanceToNow(date, { addSuffix: false });

    // Convert format to short form (1 year -> 1y, 2 days -> 2d, etc.)
    return distance
      .replace(/ years?/, "y")
      .replace(/ months?/, "mo")
      .replace(/ weeks?/, "w")
      .replace(/ days?/, "d")
      .replace(/ hours?/, "h")
      .replace(/ minutes?/, "m")
      .replace(/ seconds?/, "s")
      .replace("about ", ""); // Remove 'about' prefix
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString; // Return original string if there's an error
  }
};

const NotificationItem = ({
  // id,
  title,
  description,
  image,
  notifiedOn,
  targetId,
  type,
  onClose,
}: TNotification & {
  locale: SupportedLanguage;
  onClose: () => void;
  onRead: (id: string) => void;
}) => {
  let hasLink = false;
  let link = "";
  if (type === "InterviewDetails") {
    hasLink = true;
    link = `/schedules/${targetId}`;
  } else if (type === "ProfileDetail") {
    hasLink = true;
    link = `/profiles/${targetId}`;
  }

  return (
    <div
      className={cn(
        "flex gap-3 p-4 hover:bg-gray-50 items-center relative cursor-pointer"
        // !is_read ? "bg-primary/10" : ""
      )}
    >
      {image && (
        <span className="w-10 aspect-square">
          <Image
            src={image}
            alt={title}
            fill
            className="object-cover"
            sizes="(min-width: 768px) 50vw, 100vw"
          />
        </span>
      )}
      <span className="">
        <span className="font-bold text-base inline">{title} </span>
        <span className="text-gray-700 inline">{description}</span>
        {/* {!is_read && (
          <span
            className="text-primary text-sm hover:underline inline cursor-pointer ml-1 relative z-20"
            onClick={() => {
              onRead(id.toString());
            }}
          >
            {lang["mark-as-read"][locale] || "Mark as read"}
          </span>
        )} */}
      </span>
      <span className="ml-auto self-start text-gray-500 text-sm">
        {isValidDate(notifiedOn) ? formatRelativeTime(notifiedOn) : notifiedOn}
      </span>
      {hasLink && (
        <Link className="absolute inset-0" href={link} onClick={onClose}></Link>
      )}
    </div>
  );
};

type NotificationProps = {
  count: number;
  notifications: TNotification[];
  locale: SupportedLanguage;
};

function parseNotification(notifications: TNotification[]) {
  return notifications.map((notification) => {
    return {
      ...notification,
      // content: JSON.parse(notification.content as unknown as string),
    };
  });
}

export default function Notification({
  count,
  notifications,
  locale,
}: NotificationProps) {
  const [unreadCount, unreadCountSet] = useState<number>(count);
  const [open, openSet] = useState<boolean>(false);
  const [notificationsData, notificationsDataSet] = useState<TNotification[]>(
    () => parseNotification(notifications)
  );

  // console.log({ notificationsData });

  const [isLoading, isLoadingSet] = useState<boolean>(false);

  const fetchUnreadCount = useCallback(async () => {
    try {
      // const response = await getUnreadCounter();

      const response = {
        status: "success",
        metaData: {
          unread_count: 0,
        },
        message: "",
      };
      if (response.status === "success") {
        unreadCountSet(response.metaData.unread_count);
      }
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      isLoadingSet(true);
      const res = await getNotifications({ pageNumber: 1, pageSize: 20 });

      if (res.status === "success") {
        notificationsDataSet(parseNotification(res.metaData));
      }
      // Also update unread count
      // const response = await getUnreadCounter();

      const response = {
        status: "success",
        metaData: {
          unread_count: 0,
        },
        message: "",
      };
      if (response.status === "success") {
        unreadCountSet(response.metaData.unread_count);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      isLoadingSet(false);
    }
  }, []);

  // Short polling for notifications every 30 seconds
  useEffect(() => {
    // Initial fetch to ensure we have fresh data

    // Set up polling interval
    const intervalId = setInterval(() => {
      fetchUnreadCount();
    }, 30000); // 30 seconds

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [fetchUnreadCount]); // Empty dependency array means this effect runs once on mount

  // const handleNotificationClick = async () => {
  //   try {
  //     isLoadingSet(true);
  //     const notificationsResponse = await getNotifications();
  //     const response = await getUnreadCounter();
  //     if (notificationsResponse.status === "success") {
  //       notificationsDataSet(notificationsResponse.metaData);
  //     }
  //     if (response.status === "success") {
  //       unreadCountSet(response.metaData.unread_count);
  //     }
  //   } catch (error) {
  //     console.error("Failed to fetch unread count:", error);
  //   } finally {
  //     isLoadingSet(false);
  //   }
  // };

  // Fetch notifications when popover opens
  const handlePopoverOpenChange = async (isOpen: boolean) => {
    openSet(isOpen);
    if (isOpen) {
      fetchNotifications();
    }
  };

  function onClose() {
    openSet(false);
  }

  async function onRead() {
    // const response = await readNotificationById(Number(id));

    const response = {
      status: "success",
      metaData: {
        unread_count: 0,
      },
      message: "",
    };
    if (response.status === "success") {
      fetchUnreadCount();
      fetchNotifications();
    }
  }

  return (
    <Popover onOpenChange={handlePopoverOpenChange} open={open}>
      <PopoverTrigger asChild>
        <Button
          className="relative !px-2"
          variant="link"
          size="icon-lg"
          onClick={() => {
            openSet(!open);
          }}
          // onClick={handleNotificationClick}
          disabled={isLoading}
        >
          <NotificationIcon
            size="32"
            variant="Outline"
            className={`fill-current ${isLoading ? "opacity-70" : ""}`}
          />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-screen lg:w-[400px] p-0 rounded-xl overflow-hidden"
        align="center"
      >
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-xl font-bold">
            {lang["notification"][locale] || "Notification"}
          </h3>
          <Button
            variant="link"
            className="text-primary p-0 h-auto"
            onClick={async () => {
              // Here you would add the mark-all-read functionality
              // For now, we'll just update the unread count to 0
              unreadCountSet(0);
            }}
          >
            {lang["mark-all-read"][locale] || "Marks all read"}
          </Button>
        </div>

        {isLoading && (
          <span className="p-4 text-center justify-center flex">
            <svg
              className="mr-3 -ml-1 size-5 animate-spin text-gray-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span className="text-gray-500">Loading...</span>
          </span>
        )}

        <ScrollArea className="h-[300px]">
          {notificationsData.length === 0 ? (
            <p className="p-4 text-center">
              {lang["no-notifications"][locale] || "No notifications"}
            </p>
          ) : (
            notificationsData.map((notification) => {
              return (
                <NotificationItem
                  key={notification.id}
                  onClose={onClose}
                  onRead={onRead}
                  locale={locale}
                  {...notification}
                />
              );
            })
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
